/**
 * MyCopro - Admin Bottom Navigation
 * Barre de navigation inférieure pour l'espace Admin
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building2, 
  Briefcase, 
  Users,
  Menu
} from 'lucide-react';
import { useUIStore } from '@/stores';

// ============================================================================
// TYPES
// ============================================================================

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  path?: string;
  isActive?: boolean;
  onClick?: () => void;
}

// ============================================================================
// SOUS-COMPOSANTS
// ============================================================================

const NavItem: React.FC<NavItemProps> = ({ icon, label, path, isActive, onClick }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (path) {
      navigate(path);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`flex flex-col items-center justify-center flex-1 py-2 transition-colors ${
        isActive 
          ? 'text-indigo-600' 
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      <div className={`p-1 rounded-lg ${isActive ? 'bg-indigo-100' : ''}`}>
        {icon}
      </div>
      <span className={`text-xs mt-1 ${isActive ? 'font-medium' : ''}`}>
        {label}
      </span>
    </button>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

export const AdminBottomNav: React.FC = () => {
  const location = useLocation();
  const { openMenu } = useUIStore();

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 safe-area-bottom">
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto">
        <NavItem
          icon={<LayoutDashboard size={22} />}
          label="Dashboard"
          path="/admin"
          isActive={isActive('/admin') && !isActive('/admin/residences') && !isActive('/admin/syndics') && !isActive('/admin/users')}
        />
        <NavItem
          icon={<Building2 size={22} />}
          label="Résidences"
          path="/admin/residences"
          isActive={isActive('/admin/residences')}
        />
        <NavItem
          icon={<Briefcase size={22} />}
          label="Syndics"
          path="/admin/syndics"
          isActive={isActive('/admin/syndics')}
        />
        <NavItem
          icon={<Users size={22} />}
          label="Utilisateurs"
          path="/admin/users"
          isActive={isActive('/admin/users')}
        />
        <NavItem
          icon={<Menu size={22} />}
          label="Menu"
          onClick={openMenu}
        />
      </div>
    </nav>
  );
};

export default AdminBottomNav;
